// Create or edit a contact. People-first: only a name is required.

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { CONTACT_TYPES } from '../lib/format';
import { normalizeEmail, normalizePhone } from '../lib/import/normalize';
import { supabase, unwrap, type Tables } from '../lib/supabase';
import { Building2, X } from 'lucide-react';
import { BuildingChooser } from './BuildingChooser';
import { Button, ErrorBox, Field, FieldGroup, Input, Pill, Textarea } from './ui';

type Contact = Tables<'contacts'>;

export function ContactForm({ contact, onDone }: { contact?: Contact; onDone: (id: string) => void }) {
  const qc = useQueryClient();
  const [f, setF] = useState({
    first_name: contact?.first_name ?? '',
    last_name: contact?.last_name ?? '',
    suffix: contact?.suffix ?? '',
    salutation: contact?.salutation ?? '',
    title: contact?.title ?? '',
    contact_types: contact?.contact_types ?? [],
    tags: (contact?.tags ?? []).join(', '),
    mailing_street: contact?.mailing_street ?? '',
    mailing_city: contact?.mailing_city ?? '',
    mailing_state: contact?.mailing_state ?? '',
    mailing_zip: contact?.mailing_zip ?? '',
    notes: contact?.notes ?? '',
    email: '',
    phone: '',
  });
  // New contacts only: buildings they own, linked once the contact is saved.
  const [owned, setOwned] = useState<{ id: string; label: string }[]>([]);
  const [addingBuilding, setAddingBuilding] = useState(false);
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });

  const save = useMutation({
    mutationFn: async () => {
      const row = {
        first_name: f.first_name.trim() || null,
        last_name: f.last_name.trim() || null,
        suffix: f.suffix.trim() || null,
        salutation: f.salutation.trim() || null,
        title: f.title.trim() || null,
        contact_types: f.contact_types,
        tags: f.tags.split(',').map((t) => t.trim()).filter(Boolean),
        mailing_street: f.mailing_street.trim() || null,
        mailing_city: f.mailing_city.trim() || null,
        mailing_state: f.mailing_state.trim() || null,
        mailing_zip: f.mailing_zip.trim() || null,
        notes: f.notes.trim() || null,
      };
      if (!row.first_name && !row.last_name) throw new Error('Enter at least a first or last name.');
      if (contact) {
        unwrap(await supabase.from('contacts').update(row).eq('id', contact.id));
        return contact.id;
      }
      const email = normalizeEmail(f.email);
      if (email.problem) throw new Error(email.problem);
      const phone = normalizePhone(f.phone);
      if (f.phone.trim() && !phone.phone) throw new Error(phone.problem ?? 'Unrecognized phone number');
      const created = unwrap(await supabase.from('contacts').insert({ ...row, source: 'manual' }).select('id').single());
      if (email.email) unwrap(await supabase.from('contact_emails').insert({ contact_id: created.id, email: email.email, is_primary: true }));
      if (phone.phone)
        unwrap(
          await supabase
            .from('contact_phones')
            .insert({ contact_id: created.id, phone_e164: phone.phone.e164, extension: phone.phone.extension, phone_type: 'mobile', is_primary: true }),
        );
      if (owned.length) {
        unwrap(await supabase.from('building_ownership').insert(owned.map((b) => ({ building_id: b.id, owner_contact_id: created.id, source: 'manual' }))));
      }
      return created.id;
    },
    onSuccess: (id) => {
      qc.invalidateQueries({ queryKey: ['contacts'] });
      qc.invalidateQueries({ queryKey: ['contact', id] });
      onDone(id);
    },
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    save.mutate();
  }

  return (
    <form onSubmit={submit} className="space-y-3">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-6">
        <Field label="First name" className="col-span-1 sm:col-span-2">
          <Input value={f.first_name} onChange={set('first_name')} autoFocus />
        </Field>
        <Field label="Last name" className="col-span-1 sm:col-span-3">
          <Input value={f.last_name} onChange={set('last_name')} />
        </Field>
        <Field label="Suffix" className="col-span-1">
          <Input value={f.suffix} onChange={set('suffix')} placeholder="MD" />
        </Field>
        <Field label="Greeting" className="col-span-1 sm:col-span-3" hint='How to address them, e.g. "Dr. Patel"'>
          <Input value={f.salutation} onChange={set('salutation')} />
        </Field>
        <Field label="Title" className="col-span-2 sm:col-span-3">
          <Input value={f.title} onChange={set('title')} />
        </Field>
      </div>
      {!contact && (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="Email">
            <Input type="email" value={f.email} onChange={set('email')} />
          </Field>
          <Field label="Mobile">
            <Input value={f.phone} onChange={set('phone')} placeholder="404-555-0101" />
          </Field>
        </div>
      )}
      <FieldGroup label="Type" hint="Pick any that apply — e.g. investor and broker">
        <div className="flex flex-wrap gap-1.5">
          {CONTACT_TYPES.map((t) => {
            const on = f.contact_types.includes(t);
            return (
              <button type="button" key={t} onClick={() => setF({ ...f, contact_types: on ? f.contact_types.filter((x) => x !== t) : [...f.contact_types, t] })}>
                <Pill color={on ? 'indigo' : 'gray'} className="capitalize">
                  {t}
                </Pill>
              </button>
            );
          })}
        </div>
      </FieldGroup>
      {!contact && (
        <FieldGroup label="Buildings they own">
          {owned.length > 0 && (
            <ul className="mb-2 space-y-1">
              {owned.map((b) => (
                <li key={b.id} className="flex items-center gap-2 text-sm">
                  <Building2 className="h-3.5 w-3.5 text-slate-400" />
                  <span className="flex-1">{b.label}</span>
                  <button type="button" className="text-slate-400 hover:text-rose-600" onClick={() => setOwned(owned.filter((x) => x.id !== b.id))} aria-label="Remove">
                    <X className="h-3.5 w-3.5" />
                  </button>
                </li>
              ))}
            </ul>
          )}
          {addingBuilding ? (
            <BuildingChooser
              onChosen={(b) => {
                if (!owned.some((x) => x.id === b.id)) setOwned([...owned, b]);
                setAddingBuilding(false);
              }}
            />
          ) : (
            <Button type="button" size="sm" onClick={() => setAddingBuilding(true)}>
              + Add building
            </Button>
          )}
        </FieldGroup>
      )}
      <Field label="Tags" hint="Comma-separated">
        <Input value={f.tags} onChange={set('tags')} />
      </Field>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-6">
        <Field label="Mailing street" className="col-span-2 sm:col-span-6">
          <Input value={f.mailing_street} onChange={set('mailing_street')} />
        </Field>
        <Field label="City" className="col-span-2 sm:col-span-3">
          <Input value={f.mailing_city} onChange={set('mailing_city')} />
        </Field>
        <Field label="State" className="sm:col-span-1">
          <Input value={f.mailing_state} onChange={set('mailing_state')} />
        </Field>
        <Field label="ZIP" className="sm:col-span-2">
          <Input value={f.mailing_zip} onChange={set('mailing_zip')} />
        </Field>
      </div>
      <Field label="Notes">
        <Textarea value={f.notes} onChange={set('notes')} rows={4} />
      </Field>
      <ErrorBox error={save.error} />
      <div className="flex justify-end">
        <Button variant="primary" type="submit" busy={save.isPending}>
          {contact ? 'Save' : 'Create contact'}
        </Button>
      </div>
    </form>
  );
}
